import { ApolloClient } from '@apollo/client';
import { Dispatch } from 'redux';

import PicturesApi from 'entities/picture/api';

import { CallActionPayload, MethodResponse, UIStateCallback } from '../../types';
import { ContextAPI } from '../ImpactAiContextProvider';

export const loadPictures = async (
  payload: CallActionPayload,
  client: ApolloClient<unknown>,
  contextApi: ContextAPI,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  dispatch: Dispatch<any>,
  uiStateCallback?: UIStateCallback
): Promise<MethodResponse> => {
  const { outputPath = 'pictures', waitForCompletion = true } = payload as {
    outputPath?: string;
    waitForCompletion?: boolean;
  };

  if (uiStateCallback) {
    uiStateCallback('processing');
  }

  const performLoad = async (): Promise<void> => {
    // Fetch all pictures
    const action = await PicturesApi.fetchAll().toPromise();
    // Dispatch to Redux store
    if (action && action.type) {
      dispatch(action);
    }

    // Transform pictures to context format
    const allPictures = action?.pictures ? Object.values(action.pictures.toJS()) : [];

    const pictures = allPictures.map((picture: any) => ({
      id: picture.id,
      resource: picture.resource,
      categories: Array.isArray(picture.categories)
        ? picture.categories
        : picture.categories?.toArray?.() || [],
    }));

    // Set in context
    contextApi.setValue(outputPath, pictures);
  };

  if (!waitForCompletion) {
    performLoad()
      .then(() => {
        if (uiStateCallback) {
          uiStateCallback('complete');
        }
      })
      .catch((error) => {
        console.error('Background loadPictures failed:', error);
      });

    if (uiStateCallback) {
      uiStateCallback('complete');
    }

    return {};
  }

  try {
    await performLoad();

    if (uiStateCallback) {
      uiStateCallback('complete');
    }

    return {};
  } catch (error) {
    const handleRetry = async () => {
      try {
        return await loadPictures(payload, client, contextApi, dispatch, uiStateCallback);
      } catch (retryError) {
        if (uiStateCallback) {
          uiStateCallback('error', handleRetry);
        }
        return { error: true };
      }
    };

    if (uiStateCallback) {
      uiStateCallback('error', handleRetry);
    }
    return { error: true };
  }
};
